<template>
  <v-card class="secondary" height="100%">
    <v-form ref="form">
      <v-card-title class="font-weight-medium secondary--text primary">
        {{ edit ? `Edit Programme Scheme Term` : "Add Programme Scheme Term" }}
        <v-spacer></v-spacer>
        <v-btn small icon @click="$emit('closeDialog')">
          <v-icon small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-5">
        <v-row>
          <v-col cols="6">
            <v-autocomplete
              v-model.trim="form.program_scheme_id"
              label="Programme Scheme*"
              :items="program_schemes"
              item-text="name"
              item-value="id"
              outlined
              dense
              bottom
              hide-details="auto"
              :append-icon="program_scheme_id ? '' : 'mdi-menu-down'"
              :readonly="!!program_scheme_id"
              :rules="[rules.required]"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              v-model.number="form.term_no"
              label="Term Number*"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required, rules.number]"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              v-model.trim="form.no_of_courses"
              label="Number of Core Courses*"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required, rules.number]"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              v-model.number="form.no_of_electives"
              label="Number of Electives*"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.acceptZero]"
            />
          </v-col>
        </v-row>

        <v-row v-if="form.no_of_electives > 0">
          <v-col cols="12">
            <div class="d-flex align-center" style="gap: 8px">
              <span class="caption grey--text text--darken-1 mr-2"
                >Has streams?</span
              >
              <v-radio-group
                v-model="form.is_stream"
                row
                hide-details
                dense
                class="mt-0 pt-0"
              >
                <v-radio label="No" :value="false" />
                <v-radio label="Yes" :value="true" />
              </v-radio-group>
            </div>
          </v-col>

          <v-col cols="6" v-if="form.is_stream">
            <v-text-field
              v-model.number="form.stream_credits"
              label="Total Credits per Stream*"
              outlined
              dense
              hide-details="auto"
              persistent-hint
              :rules="[rules.required, rules.number]"
            />
          </v-col>

          <v-col cols="12" v-if="form.is_stream">
            <v-sheet outlined rounded class="pa-3">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="d-flex align-center">
                  <span class="caption grey--text text--darken-1">Streams</span>
                  <v-chip
                    x-small
                    class="ml-2"
                    color="primary"
                    text-color="white"
                  >
                    {{ form.stream_names.length }}
                  </v-chip>
                </div>
              </div>

              <div
                v-if="form.stream_names.length"
                class="stream-chips-container mb-3"
              >
                <v-chip
                  v-for="(stream, index) in form.stream_names"
                  :key="index"
                  close
                  outlined
                  class="mr-2 mb-2 stream-chip"
                  @click:close="removeStream(index)"
                >
                  {{ stream || `Stream ${index + 1}` }}
                </v-chip>
              </div>

              <div v-else class="text-center pa-3">
                <v-icon color="grey lighten-2" size="36"
                  >mdi-puzzle-outline</v-icon
                >
                <p class="caption grey--text mb-0 mt-1">No streams added yet</p>
              </div>

              <div class="d-flex align-center" style="gap: 8px">
                <v-text-field
                  v-model="newStreamName"
                  label="Enter stream name"
                  outlined
                  dense
                  hide-details
                  @keyup.enter="addNewStream"
                  class="flex-grow-1"
                />
                <v-btn
                  depressed
                  color="primary"
                  :disabled="!newStreamName.trim()"
                  @click="addNewStream"
                >
                  Add Stream
                </v-btn>
              </div>
            </v-sheet>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pt-0 mb-3 mx-2">
        <v-spacer></v-spacer>
        <v-btn class="primary" @click="save">
          {{ edit ? "Update" : "Save" }}
        </v-btn>
      </v-card-actions>
    </v-form>

    <v-dialog v-model="confirmDialog.show" max-width="420px" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          Confirm Stream Change
        </v-card-title>
        <v-card-text>
          Changing this will delete all existing elective courses for this term.
          Are you sure you want to <strong>{{ confirmDialog.action }}</strong
          >?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="onConfirmCancel">Cancel</v-btn>
          <v-btn color="error" depressed @click="onConfirmProceed"
            >Confirm</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { rules } from "@/helpers/rules";

export default {
  props: {
    program_scheme_term: { type: Object, default: () => ({}) },
    program_schemes: { type: Array, default: () => [] },
    edit: { type: Boolean, default: false },
    program_scheme_id: { type: Number, default: null },
    termNo: { type: Number, default: null },
    pst_courses: { type: Array, default: () => [] },
  },

  data() {
    return {
      rules,
      newStreamName: "",
      _revertingStream: false,
      form: {
        department_id: null,
        program_scheme_id: null,
        term_no: null,
        no_of_courses: null,
        no_of_electives: null,
        is_stream: false,
        stream_names: [],
        stream_credits: null,
      },
      confirmDialog: {
        show: false,
        action: "",
        pendingNewVal: null,
        pendingOldVal: null,
      },
    };
  },

  watch: {
    "form.is_stream"(newVal, oldVal) {
      if (this._revertingStream) {
        this._revertingStream = false;
        return;
      }

      if (oldVal === undefined || oldVal === newVal) return;

      if (this.edit) {
        this.confirmDialog = {
          show: true,
          action: newVal ? "enable streams" : "disable streams",
          pendingNewVal: newVal,
          pendingOldVal: oldVal,
        };
        return;
      }

      if (!newVal) {
        this.form.stream_names = [];
        this.newStreamName = "";
      }
    },
  },

  created() {
    this._revertingStream = true;
    this.init();
    if (this.program_scheme_id) {
      this.form.program_scheme_id = this.program_scheme_id;
    }
    this.$nextTick(() => {
      this._revertingStream = false;
    });
  },

  methods: {
    init() {
      if (this.edit) {
        this.form = {
          ...this.program_scheme_term,
          is_stream: this.program_scheme_term.is_stream ?? false,
          stream_names:
            this.program_scheme_term.stream_names ??
            this.program_scheme_term.streams ??
            [],
        };
      } else {
        this.form.term_no = this.termNo;
      }
    },

    onConfirmProceed() {
      this.confirmDialog.show = false;
      if (!this.confirmDialog.pendingNewVal) {
        this.form.stream_names = [];
        this.newStreamName = "";
      }
    },

    onConfirmCancel() {
      this.confirmDialog.show = false;
      this._revertingStream = true;
      this.$nextTick(() => {
        this.form.is_stream = this.confirmDialog.pendingOldVal;
        if (this.confirmDialog.pendingOldVal) {
          this.form.stream_names =
            this.program_scheme_term.stream_names ??
            this.program_scheme_term.streams ??
            [];
        }
      });
    },

    save() {
      if (!this.$refs.form.validate()) return;

      const submitData = {
        ...this.form,
        stream_names: this.form.is_stream ? this.form.stream_names : null,
        stream_credits: this.form.is_stream ? this.form.stream_credits : null,
        streams: undefined,
      };

      this.$emit(this.edit ? "update" : "create", submitData);
    },

    addNewStream() {
      const name = this.newStreamName.trim();
      if (name) {
        this.form.stream_names.push(name);
        this.newStreamName = "";
      }
    },

    removeStream(index) {
      this.form.stream_names.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.stream-chips-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 4px 0;
  min-height: 48px;
}

.stream-chip {
  transition: all 0.2s ease;
  border-radius: 16px;
}

.stream-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stream-chip >>> .v-chip__close {
  color: rgba(0, 0, 0, 0.54);
  transition: color 0.2s ease;
}

.stream-chip >>> .v-chip__close:hover {
  color: #f44336;
}
</style>
